import { Icon } from '@acme/assets';
import {
  CurrencyFilter,
  CurrencyResponse,
  ECurrencyTypeEnum,
  PagedGetCurrencyResponse,
} from '@acme/domains';
import { ResultModal, TextAreaModal, useModal } from '@acme/foundation';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { CurrencyForm } from './currencyForm';
import { CurrencyService } from '@acme/api';
import { useNavigate } from 'react-router-dom';

interface CurrencyListProps {
  loader: (
    filter: CurrencyFilter
  ) => Promise<PagedGetCurrencyResponse | undefined>;
  onRowClick?: (currencyId: string) => void;
}

export const CurrencyList = ({ loader, onRowClick }: CurrencyListProps) => {
  const [data, setData] = useState<PagedGetCurrencyResponse | null>();
  const { openModal, closeModal, openConfirmModal } = useModal();
  const navigate = useNavigate();
  const [filter, setFilter] = useState({});
  const loadData = useCallback(async () => {
    const response = await loader({ ...filter });
    setData(response);
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onCreateCurrencyClick = () => {
    openModal({
      content: (
        <CurrencyForm
          onClose={closeModal}
          onSubmit={CurrencyService.postApiV1CurrencyCreate}
        />
      ),
      modalType: 'custom',
    });
  };
  const onEditCurrencyClick = (rowData: CurrencyResponse) => {
    openModal({
      content: (
        <CurrencyForm
          onClose={closeModal}
          onSubmit={CurrencyService.putApiV1CurrencyUpdate}
          defaultData={rowData}
        />
      ),
      modalType: 'custom',
    });
  };
  const onStatusSubmit = (rowData: CurrencyResponse) => {
    CurrencyService.putApiV1CurrencySetStatus({
      requestBody: {
        id: rowData.id,
        type: rowData.type,
        isActive: !rowData.isActive,
      },
    })
      .then(() => {
        openModal({
          content: (
            <ResultModal
              title={'Успешно'}
              description={
                rowData.isActive
                  ? 'Валюта была заблокирована'
                  : 'Валюта была добавлена в список валют'
              }
              type={'success'}
              onBack={() => {
                closeModal();
                loadData();
              }}
            />
          ),
          opaqueBackground: true,
        });
      })
      .catch(() => {
        openModal({
          content: (
            <ResultModal
              title={
                rowData.isActive
                  ? 'Заблокировать валюту не удалось'
                  : 'Разблокировать валюту не удалось'
              }
              description={'Попробуйте повторить позднее'}
              type={'error'}
              onBack={() => {
                navigate('/rates/currencyList');
                closeModal();
              }}
            />
          ),
          opaqueBackground: true,
        });
      });
  };
  const onSetStatusClick = async (rowData: CurrencyResponse) => {
    if (rowData.isActive) {
      openModal({
        content: (
          <TextAreaModal
            title="Блокировка валюты"
            areaLabel="Напишите причину блокировки валюты"
            approveButtonText="Заблокировать"
            onClose={closeModal}
            onApprove={() => {
              closeModal();
              onStatusSubmit(rowData);
            }}
          />
        ),
        modalType: 'custom',
      });
    } else {
      const confirmed = await openConfirmModal({
        title: 'Восстановить валюту?',
        content: 'Валюта будет доступна для работы с ней на платформе',
        okBtn: 'Подтвердить',
        cancelBtn: 'Отмена',
      });
      if (!confirmed) return;
      onStatusSubmit(rowData);
    }
  };
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex items-center gap-4 p-4">
        <Button color="primary" onClick={onCreateCurrencyClick}>
          Создать новую валюту
        </Button>
        <TextInput
          className="ml-auto"
          icon={() => <Icon name="search-outline" />}
          onChange={(e) =>
            setFilter((prev) => ({ ...prev, search: e.target.value }))
          }
          placeholder="Наименование валюты"
        />
      </div>
      <div className="overflow-x-auto">
        <Table hoverable>
          <TableHead>
            <TableRow>
              <TableHeadCell></TableHeadCell>
              <TableHeadCell></TableHeadCell>
              <TableHeadCell>Сокр. наименование валюты</TableHeadCell>
              <TableHeadCell>Полное наименование валюты</TableHeadCell>
              <TableHeadCell>Тип валюты</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody className="divide-y">
            {data?.items?.map((currency) => (
              <TableRow
                key={currency.id}
                onClick={() => onRowClick?.(currency.name || '')}
                className="text-sm font-normal text-gray-900 dark:text-white"
              >
                <TableCell className="w-[60px] text-gray-500">
                  <button onClick={() => onEditCurrencyClick(currency)}>
                    <Icon name="edit-outline" />
                  </button>
                </TableCell>
                <TableCell className="w-[60px] text-gray-500">
                  <button onClick={() => onSetStatusClick(currency)}>
                    {currency.isActive ? (
                      <Icon name="unlock-outline" />
                    ) : (
                      <Icon name="lock-outline" color="#E02424" />
                    )}
                  </button>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {currency.code}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {currency.name}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {currency.type === ECurrencyTypeEnum.Fiat
                    ? 'Фиатная'
                    : 'Цифровая'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
